/* eslint-disable max-len */

import { HttpResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ApiService } from '@api/api.service';
import { VerificationRequest } from '@api/oauth/token/token.service';
import { AccountE2eEncryption } from '@api/types';
import { TwoFactorMethod } from '@api/users/me/twoFactor/two-factor.service';
import { ModalService } from '@shared/modal/modal.service';
import { EchoService } from '@shared/services/echo.service';
import { EncryptionHelperService } from '@shared/services/encryption-helper.service';
import { HashService } from '@shared/services/hash.service';
import { StatusCodes } from '@shared/types/status-codes';
import { TaggedError } from '@shared/types/tagged-error';
import { Subscription } from 'rxjs';
import { LocalStorageService } from './../../../shared/services/local-storage.service';

@Component({
  selector: 'rav-e2e-encryption-modal',
  templateUrl: './e2e-encryption-modal.component.html',
  styleUrls: ['./e2e-encryption-modal.component.scss']
})
export class E2eEncryptionModalComponent implements OnInit {
  @Input() isEnable = true;

  accountId: string;
  e2eEncryptionFormGroup: FormGroup;
  accountEncryptionEnabled: AccountE2eEncryption = AccountE2eEncryption.Inactive;
  production: boolean;
  subscriptions: Subscription[] = [];
  containsInvalidUsers = false;
  error = '';
  errorMessage: string;
  isProcessing: boolean;

  modalText: any;
  modalTextEnable = {
    featureTerms: [
      'I understand that enabling end-to-end document encryption will add multiple layers of additional encryption to all documents.',
      'I understand that this setting will apply to documents for all users on the account.',
      'I understand that some features may be incompatible such as combining documents and downloading multiple documents at the same time.',
      'I understand that users will be unable to remain logged in after closing browser sessions and will need to log in again for each Raven Cloud session.',
      'I understand that if all Account Owners lose access to, or forget their passwords, it will be impossible for Raven to restore access or recover account data.',
      'I understand that as long as I have end-to-end document encryption enabled, I will not be able to enable the password reset policy.'
    ],
    action: 'enable',
    action2: 'enabling'
  };
  modalTextDisable = {
    featureTerms: [
      'I understand that disabling end-to-end document encryption will decrypt layers of private encryption for documents, although documents will remain encrypted at rest and in transit using standard encryption.',
      'I understand that this setting will apply to documents for all users on the account.',
      'I understand that the decryption process will take time and could take an extended length of time to complete depending on the total number of documents in the account.',
      'I understand that end-to-end encryption cannot be re-enabled until the decryption process completes.'
    ],
    action: 'disable',
    action2: 'disabling'
  };

  // 2fa params
  twoFARequest: VerificationRequest;
  TwoFactorMethod = TwoFactorMethod;
  display2FAForm = false;
  useBackupCode = false;
  is2FAModal = false;
  verificationCode: string;
  backupCode: string;

  private password: string;
  private wrongPassError = 'Password is incorrect';
  private inProgressError = 'Your files are being processed at the moment. Please try again later.';
  private invalidUsersError =
    // tslint:disable-next-line: max-line-length
    'One or more users are inactive and require private user keys. Please ask all users to log in to their accounts or remove inactive users before enabling End to End Encryption. Alternatively, click "Send Notice" below to email users with a link to log in and generate private user keys.';

  constructor(
    private formBuilder: FormBuilder,
    private modal: ModalService,
    private hashService: HashService,
    private api: ApiService,
    private encryptionHelper: EncryptionHelperService,
    private changeDetector: ChangeDetectorRef,
    private localStorage: LocalStorageService,
    private echo: EchoService
  ) {}

  ngOnInit() {
    this.initializeModal(this.isEnable);
    this.modal.open.subscribe(() => {
      this.initializeModal(this.isEnable);
    });
    this.accountId = this.localStorage.userWithToken.account_id;
  }

  initializeModal(isEnable: boolean) {
    this.is2FAModal = false;
    this.error = '';
    this.containsInvalidUsers = false;
    this.display2FAForm = false;
    this.verificationCode = '';
    this.isProcessing = false;
    this.buildFormGroup();
    this.modalText = isEnable ? this.modalTextDisable : this.modalTextEnable;
  }
  /**
   * creates the form group with validator to be displayed.
   */
  buildFormGroup(): void {
    if (this.isEnable) {
      this.e2eEncryptionFormGroup = this.formBuilder.group({
        box0: new FormControl('', Validators.requiredTrue),
        box1: new FormControl('', Validators.requiredTrue),
        box2: new FormControl('', Validators.requiredTrue),
        box3: new FormControl('', Validators.requiredTrue),
        password: new FormControl('', Validators.required),
        verificationCode: new FormControl()
      });
    } else {
      this.e2eEncryptionFormGroup = this.formBuilder.group({
        box0: new FormControl('', Validators.requiredTrue),
        box1: new FormControl('', Validators.requiredTrue),
        box2: new FormControl('', Validators.requiredTrue),
        box3: new FormControl('', Validators.requiredTrue),
        box4: new FormControl('', Validators.requiredTrue),
        box5: new FormControl('', Validators.requiredTrue),
        password: new FormControl('', Validators.required),
        verificationCode: new FormControl()
      });
    }
  }

  /**
   * Makes api request to update the encryption on the account.
   *
   * @param value form values entered by the user.
   */
  async enableDocumentEncryption(value: any): Promise<void> {
    this.isProcessing = true;
    // Validations needed previous to enabling e2e encryption
    try {
      if (!(await this.validatePreEncryptionEnabling(value.password))) return;
    } catch (e) {
      this.modal.close.next({
        message: `There seems to be an error enabling End-to-End Encryption for your Account.
          Please contact Raven Support to further assist with enabling for your account or try again later.`,
        messageType: 'error'
      });
      throw new TaggedError(e.message, { e2eEncryption: 'validatePreEncryptionEnabling' });
    }

    const passwordHash = await this.hashService.hash(value.password);
    this.api.accounts.byId.e2e_encryption
      .post(this.accountId, value.password, passwordHash, value.verification_code)
      .subscribe(
        (response: HttpResponse<any>) => {
          if (response.status === StatusCodes.TWO_FA_CODE_REQUIRED) {
            this.twoFARequest = <VerificationRequest>response.body;
            this.display2FAForm = true;
            this.is2FAModal = true;
            this.changeDetector.detectChanges();

            return true;
          }
          this.accountEncryptionEnabled = AccountE2eEncryption.Activating;
          this.modal.close.next({
            message: 'Encryption enabled successfully.',
            messageType: 'success'
          });
          this.echo.encryptionDecryptionProgressListener(AccountE2eEncryption.Activating);
          this.setStatusAndCleanup();
        },
        (e) => {
          this.isProcessing = false;
          if (e.status === StatusCodes.INVALID_DATA) {
            this.error = this.wrongPassError;
          } else if (e.status === StatusCodes.FORBIDDEN) {
            this.error = this.inProgressError;
          } else {
            this.modal.close.next({
              message: e.message,
              messageType: 'error'
            });
            throw new TaggedError(e.message, { e2eEncryption: 'enableDocumentEncryption' });
          }
        }
      );
  }

  async validatePreEncryptionEnabling(pass: string) {
    if (pass === '') return false;

    // makes sure that every user has their corresponding user keys, otherwise let the user know
    if (!(await this.encryptionHelper.allUsersInAccountHaveUserKeys())) {
      this.isProcessing = false;
      this.error = this.invalidUsersError;
      this.containsInvalidUsers = true;
      this.password = pass;
      return false;
    }

    // generate account's user's account Keys and saves them to the API
    await this.encryptionHelper.createAndSaveAccountKeyPairs();

    // run a test to make sure that the generated keys are valid and encryption process work
    await this.encryptionHelper.testEncryptionProcess();
    return true;
  }

  async sendNoticeAction(): Promise<void> {
    const passwordHash = await this.hashService.hash(this.password);
    this.api.users.requestLogin.post(passwordHash).subscribe(
      () => {
        alert('Email sent to the users.');
        this.closeDialog();
      },
      (error) => {
        if (error.status === StatusCodes.INVALID_DATA) {
          this.error = this.wrongPassError;
          return;
        }
        alert('Error sending the email, please contact support');
      }
    );
  }

  /**
   * Makes api request to disable the e2e encryption on the account.
   *
   * @param value form values entered by the user.
   */
  async disableDocumentEncryption(value: any): Promise<void> {
    this.isProcessing = true;
    if (value.password === '') return;
    const passwordHash = await this.hashService.hash(value.password);
    try {
      await this.encryptionHelper.setDisableKeyPair(passwordHash);
    } catch (e) {
      this.modal.close.next({
        message: `There seems to be an error disabling End-to-End Encryption for your Account.
        Please contact Raven Support to further assist with disabling for your account or try again later.`,
        messageType: 'error'
      });
      throw new TaggedError(e.message, { e2eEncryption: 'setDisableLeyPair' });
    }
    this.api.accounts.byId.e2e_encryption
      .delete(this.accountId, value.password, passwordHash, value.verification_code)
      .subscribe(
        (response: HttpResponse<any>) => {
          if (response.status === StatusCodes.TWO_FA_CODE_REQUIRED) {
            this.twoFARequest = <VerificationRequest>response.body;
            this.display2FAForm = true;
            this.is2FAModal = true;
            this.changeDetector.detectChanges();

            return true;
          }
          this.accountEncryptionEnabled = AccountE2eEncryption.Deactivating;
          this.modal.close.next({
            message: 'Encryption disabled successfully.',
            messageType: 'success'
          });
          this.echo.encryptionDecryptionProgressListener(AccountE2eEncryption.Deactivating);
          this.setStatusAndCleanup();
        },
        (e) => {
          this.isProcessing = false;
          if (e.status === StatusCodes.INVALID_DATA) {
            this.error = this.wrongPassError;
          } else if (e.status === StatusCodes.FORBIDDEN) {
            this.error = this.inProgressError;
          } else {
            this.modal.close.next({
              message: e.message,
              messageType: 'error'
            });
            throw new TaggedError(e.message, { e2eEncryption: 'disableDocumentEncryption' });
          }
        },
        () => {
          this.setStatusAndCleanup();
        }
      );
  }

  /**
   * sets the e2e encryption status for the account and resets the form
   */
  setStatusAndCleanup() {
    this.isProcessing = false;
    this.encryptionHelper.accountEncryptionStatus.next(this.accountEncryptionEnabled);
    if (!this.is2FAModal) this.e2eEncryptionFormGroup.reset();
  }

  /**
   * Clean error on changes.
   */
  cleanError() {
    if (this.errorMessage) this.errorMessage = null;
  }

  async twoFaLogin(values: any): Promise<void> {
    this.is2FAModal = false;
    this.verificationCode = values.verification_code;
  }

  closeDialog(): void {
    this.e2eEncryptionFormGroup.reset();
    this.modal.close.next();
  }

  formUpdated() {
    this.error = '';
    this.containsInvalidUsers = false;
  }
}
